import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Label } from '../components/ui/label';
import { BankConnectionManager } from '../components/BankConnectionManager';
import { User, Link2, Palette, Sun, Moon, Monitor, Sparkles, Leaf, Mountain, Briefcase, ChevronRight, Bell } from 'lucide-react';
import {
  ThemeCategory,
  THEME_CATEGORIES,
  Theme,
  apiService,
  WeeklyInsightsSettings,
  UpdateWeeklyInsightsSettingsRequest,
  MonthlyRoundupSettings,
  UpdateMonthlyRoundupSettingsRequest,
} from '../services/api';

type SettingsSection = 'profile' | 'integrations' | 'themes' | 'notifications';

// Category icons mapping
const categoryIcons: Record<ThemeCategory, React.FC<{ className?: string }>> = {
  vibrant: Sparkles,
  earthy: Mountain,
  nature: Leaf,
  professional: Briefcase,
};

// Themes Section Component
interface ThemesSectionProps {
  currentTheme: Theme | null;
  availableThemes: Theme[];
  updatingTheme: string | null;
  themeModePreference: 'light' | 'dark' | 'system';
  onThemeClick: (themeId: string, themeMode?: 'light' | 'dark' | 'system') => void;
}

const ThemesSection: React.FC<ThemesSectionProps> = ({
  currentTheme,
  availableThemes,
  updatingTheme,
  themeModePreference,
  onThemeClick,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ThemeCategory>(
    currentTheme?.category || 'professional'
  );

  const categoryThemes = useMemo(() => {
    return availableThemes.filter(t => t.category === selectedCategory);
  }, [availableThemes, selectedCategory]);

  const lightTheme = categoryThemes.find(t => t.mode === 'light');
  const darkTheme = categoryThemes.find(t => t.mode === 'dark');

  return (
    <div className="fade-in-up">
      <div className="mb-6">
        <h1 
          className="text-xl md:text-2xl font-bold tracking-tight mb-1"
          style={{ color: 'var(--theme-text)' }}
        >
          Themes
        </h1>
        <p 
          className="text-sm"
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          Choose a color palette that suits your style
        </p>
      </div>
      
      {/* Category Selection */}
      <div className="mb-8">
        <Label 
          className="text-xs font-semibold uppercase tracking-wider mb-3 block"
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          Choose Your Vibe
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {THEME_CATEGORIES.map((category) => {
            const IconComponent = categoryIcons[category.id];
            const isSelected = selectedCategory === category.id;
            const categoryLightTheme = availableThemes.find(
              t => t.category === category.id && t.mode === 'light'
            );
            const categoryDarkTheme = availableThemes.find(
              t => t.category === category.id && t.mode === 'dark'
            );
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`relative group p-4 rounded-2xl transition-all duration-300 text-left overflow-hidden ${
                  isSelected ? 'shadow-lg scale-[1.02]' : 'hover:shadow-md hover:scale-[1.01]'
                }`}
                style={{ 
                  backgroundColor: 'var(--theme-surface)',
                  border: isSelected 
                    ? '2px solid var(--theme-primary)' 
                    : '2px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)'
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-1.5 flex">
                  <div className="flex-1" style={{ backgroundColor: categoryLightTheme?.primary_color }} />
                  <div className="flex-1" style={{ backgroundColor: categoryDarkTheme?.primary_color }} />
                </div>
                
                <div className="flex items-center gap-2 mt-1">
                  <span style={{ color: isSelected ? 'var(--theme-primary)' : 'var(--theme-text-secondary)' }}>
                    <IconComponent className="h-5 w-5 transition-colors" />
                  </span>
                  <span className="font-semibold text-sm" style={{ color: 'var(--theme-text)' }}>
                    {category.name}
                  </span>
                </div>
                <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
                  {category.description}
                </p>
                
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--theme-primary)' }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Light/Dark Mode Selection */}
      <div className="mb-6">
        <Label 
          className="text-xs font-semibold uppercase tracking-wider mb-3 block"
          style={{ color: 'var(--theme-text-secondary)' }}
        >
          Select Mode
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            disabled={!lightTheme || !!updatingTheme}
            onClick={() => lightTheme && onThemeClick(lightTheme.id, 'light')}
            className={`relative w-full text-left p-4 rounded-2xl transition-all duration-200 ${
              themeModePreference === 'light' ? 'shadow-md ring-2 ring-offset-2' : 'hover:shadow-sm'
            } ${!lightTheme || updatingTheme ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              backgroundColor: 'var(--theme-surface)',
              border: themeModePreference === 'light'
                ? '2px solid var(--theme-primary)'
                : '2px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
              '--tw-ring-color': 'var(--theme-primary)',
              '--tw-ring-offset-color': 'var(--theme-background)',
            } as React.CSSProperties}
          >
            <div className="flex items-center gap-2">
              <Sun className="h-5 w-5" style={{ color: 'var(--theme-text-secondary)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>Light</p>
                <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Always light</p>
              </div>
            </div>
          </button>

          <button
            disabled={!darkTheme || !!updatingTheme}
            onClick={() => darkTheme && onThemeClick(darkTheme.id, 'dark')}
            className={`relative w-full text-left p-4 rounded-2xl transition-all duration-200 ${
              themeModePreference === 'dark' ? 'shadow-md ring-2 ring-offset-2' : 'hover:shadow-sm'
            } ${!darkTheme || updatingTheme ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              backgroundColor: 'var(--theme-surface)',
              border: themeModePreference === 'dark'
                ? '2px solid var(--theme-primary)'
                : '2px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
              '--tw-ring-color': 'var(--theme-primary)',
              '--tw-ring-offset-color': 'var(--theme-background)',
            } as React.CSSProperties}
          >
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5" style={{ color: 'var(--theme-text-secondary)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>Dark</p>
                <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Always dark</p>
              </div>
            </div>
          </button>

          <button
            disabled={!lightTheme || !!updatingTheme}
            onClick={() => {
              // For "system", store the category palette via the light theme ID, and let ThemeContext
              // choose light/dark variant based on prefers-color-scheme.
              if (lightTheme) onThemeClick(lightTheme.id, 'system');
            }}
            className={`relative w-full text-left p-4 rounded-2xl transition-all duration-200 ${
              themeModePreference === 'system' ? 'shadow-md ring-2 ring-offset-2' : 'hover:shadow-sm'
            } ${!lightTheme || updatingTheme ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              backgroundColor: 'var(--theme-surface)',
              border: themeModePreference === 'system'
                ? '2px solid var(--theme-primary)'
                : '2px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
              '--tw-ring-color': 'var(--theme-primary)',
              '--tw-ring-offset-color': 'var(--theme-background)',
            } as React.CSSProperties}
          >
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5" style={{ color: 'var(--theme-text-secondary)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>System</p>
                <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Match device</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Current Theme Preview */}
      {currentTheme && (
        <div 
          className="mt-8 pt-6"
          style={{ borderTop: '1px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}
        >
          <Label 
            className="text-xs font-semibold uppercase tracking-wider mb-3 block"
            style={{ color: 'var(--theme-text-secondary)' }}
          >
            Active Theme Preview
          </Label>
          <div 
            className="rounded-2xl overflow-hidden shadow-sm"
            style={{ 
              backgroundColor: currentTheme.background_color,
              border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)'
            }}
          >
            <div 
              className="p-4"
              style={{ 
                backgroundColor: currentTheme.surface_color,
                borderBottom: `1px solid ${currentTheme.text_secondary_color}22`
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: currentTheme.text_color }}>
                    {currentTheme.name}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: currentTheme.text_secondary_color }}>
                    {currentTheme.description}
                  </p>
                </div>
                <div 
                  className="px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ 
                    backgroundColor: currentTheme.primary_color,
                    color: currentTheme.mode === 'dark' ? currentTheme.background_color : '#FFFFFF'
                  }}
                >
                  Active
                </div>
              </div>
            </div>
            
            <div className="p-4 space-y-3">
              <div 
                className="flex items-center justify-between p-3 rounded-xl"
                style={{ backgroundColor: currentTheme.surface_color }}
              >
                <div>
                  <p className="text-sm font-medium" style={{ color: currentTheme.text_color }}>Coffee Shop</p>
                  <p className="text-xs" style={{ color: currentTheme.text_secondary_color }}>Today</p>
                </div>
                <span className="text-sm font-semibold" style={{ color: currentTheme.expense_color }}>-$4.50</span>
              </div>
              
              <div 
                className="flex items-center justify-between p-3 rounded-xl"
                style={{ backgroundColor: currentTheme.surface_color }}
              >
                <div>
                  <p className="text-sm font-medium" style={{ color: currentTheme.text_color }}>Salary Deposit</p>
                  <p className="text-xs" style={{ color: currentTheme.text_secondary_color }}>Yesterday</p>
                </div>
                <span className="text-sm font-semibold" style={{ color: currentTheme.income_color }}>+$3,200.00</span>
              </div>
              
              <div className="flex gap-2 pt-2">
                <div 
                  className="flex-1 py-2.5 rounded-xl text-center text-xs font-semibold"
                  style={{ 
                    backgroundColor: currentTheme.primary_color,
                    color: currentTheme.mode === 'dark' ? currentTheme.background_color : '#FFFFFF'
                  }}
                >
                  Primary Button
                </div>
                <div 
                  className="flex-1 py-2.5 rounded-xl text-center text-xs font-semibold border-2"
                  style={{ 
                    backgroundColor: 'transparent',
                    borderColor: currentTheme.accent_color,
                    color: currentTheme.accent_color
                  }}
                >
                  Secondary
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const settingsSections = [
  { id: 'profile' as const, label: 'Profile', icon: User, description: 'Your account info' },
  { id: 'integrations' as const, label: 'Integrations', icon: Link2, description: 'Bank connections' },
  { id: 'notifications' as const, label: 'Notifications', icon: Bell, description: 'Email roundups' },
  { id: 'themes' as const, label: 'Themes', icon: Palette, description: 'Personalize colors' },
];

const WEEKDAY_OPTIONS: { id: string; label: string }[] = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' },
];

export const SettingsPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { currentTheme, availableThemes, updateTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');
  const [updatingTheme, setUpdatingTheme] = useState<string | null>(null);
  const [weeklySettings, setWeeklySettings] = useState<WeeklyInsightsSettings | null>(null);
  const [loadingWeeklySettings, setLoadingWeeklySettings] = useState<boolean>(false);
  const [savingWeeklySettings, setSavingWeeklySettings] = useState<boolean>(false);
  const [weeklyError, setWeeklyError] = useState<string | null>(null);

  const [monthlySettings, setMonthlySettings] = useState<MonthlyRoundupSettings | null>(null);
  const [loadingMonthlySettings, setLoadingMonthlySettings] = useState<boolean>(false);
  const [savingMonthlySettings, setSavingMonthlySettings] = useState<boolean>(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);

  const handleThemeClick = async (themeId: string, themeMode?: 'light' | 'dark' | 'system') => {
    if (updatingTheme || currentTheme?.id === themeId) return;
    
    try {
      setUpdatingTheme(themeId);
      await updateTheme(themeId, themeMode);
    } catch (error) {
      console.error('Failed to update theme:', error);
    } finally {
      setUpdatingTheme(null);
    }
  };

  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  const formatHour = (hour: number) => {
    const normalized = ((hour % 24) + 24) % 24;
    const displayHour = normalized % 12 || 12;
    const suffix = normalized >= 12 ? 'PM' : 'AM';
    return `${displayHour}:00 ${suffix}`;
  };

  const loadWeeklySettings = useCallback(async () => {
    setLoadingWeeklySettings(true);
    setWeeklyError(null);
    try {
      const response = await apiService.getWeeklyInsightsSettings();
      setWeeklySettings(response.settings);
    } catch (error) {
      console.error('Failed to load weekly settings', error);
      setWeeklyError('Failed to load weekly insights settings.');
    } finally {
      setLoadingWeeklySettings(false);
    }
  }, []);

  const loadMonthlySettings = useCallback(async () => {
    setLoadingMonthlySettings(true);
    setMonthlyError(null);
    try {
      const response = await apiService.getMonthlyRoundupSettings();
      setMonthlySettings(response.settings);
    } catch (error) {
      console.error('Failed to load monthly settings', error);
      setMonthlyError('Failed to load monthly roundup settings.');
    } finally {
      setLoadingMonthlySettings(false);
    }
  }, []);

  useEffect(() => {
    loadWeeklySettings();
    loadMonthlySettings();
  }, [loadWeeklySettings, loadMonthlySettings]);

  const saveWeeklySettingsOptimistic = async (nextSettings: WeeklyInsightsSettings, previousSettings: WeeklyInsightsSettings) => {
    setSavingWeeklySettings(true);
    setWeeklyError(null);
    const payload: UpdateWeeklyInsightsSettingsRequest = {
      enabled: nextSettings.enabled,
      days: nextSettings.days,
      send_hour: nextSettings.send_hour,
      timezone: nextSettings.timezone,
    };
    try {
      const response = await apiService.updateWeeklyInsightsSettings(payload);
      setWeeklySettings(response.settings);
    } catch (error) {
      console.error('Failed to save weekly settings', error);
      setWeeklySettings(previousSettings);
      setWeeklyError('Failed to save weekly insights settings.');
    } finally {
      setSavingWeeklySettings(false);
    }
  };

  const toggleDay = (dayId: string) => {
    if (!weeklySettings || savingWeeklySettings) return;
    const currentDays = weeklySettings.days;
    const exists = currentDays.includes(dayId);
    const nextDays = exists ? currentDays.filter(d => d !== dayId) : [...currentDays, dayId];
    const nextSettings = { ...weeklySettings, days: nextDays.length ? nextDays : ['monday'] };
    setWeeklySettings(nextSettings);
    saveWeeklySettingsOptimistic(nextSettings, weeklySettings);
  };

  const selectAllDays = () => {
    if (!weeklySettings || savingWeeklySettings) return;
    const nextSettings = { ...weeklySettings, days: WEEKDAY_OPTIONS.map(d => d.id) };
    setWeeklySettings(nextSettings);
    saveWeeklySettingsOptimistic(nextSettings, weeklySettings);
  };

  const handleHourChange = (hour: number) => {
    if (!weeklySettings || savingWeeklySettings) return;
    const nextSettings = { ...weeklySettings, send_hour: hour };
    setWeeklySettings(nextSettings);
    saveWeeklySettingsOptimistic(nextSettings, weeklySettings);
  };

  const handleToggleWeeklyEnabled = () => {
    if (!weeklySettings || savingWeeklySettings) return;
    const nextSettings = { ...weeklySettings, enabled: !weeklySettings.enabled };
    setWeeklySettings(nextSettings);
    saveWeeklySettingsOptimistic(nextSettings, weeklySettings);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-full" style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Mobile Tab Navigation */}
      <div 
        className="md:hidden px-4 py-4"
        style={{ 
          backgroundColor: 'var(--theme-surface)',
          borderBottom: '1px solid color-mix(in srgb, var(--theme-text-secondary) 12%, transparent)'
        }}
      >
        <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--theme-text)' }}>Settings</h2>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {settingsSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap"
              style={{ 
                backgroundColor: activeSection === section.id 
                  ? 'var(--theme-primary)' 
                  : 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                color: activeSection === section.id 
                  ? (currentTheme?.mode === 'dark' ? 'var(--theme-background)' : '#FFFFFF')
                  : 'var(--theme-text-secondary)'
              }}
            >
              <section.icon className="h-4 w-4" />
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Settings Sidebar */}
      <aside 
        className="hidden md:flex flex-col w-72 flex-shrink-0"
        style={{ 
          backgroundColor: 'var(--theme-surface)',
          borderRight: '1px solid color-mix(in srgb, var(--theme-text-secondary) 12%, transparent)'
        }}
      >
        <div className="p-6">
          <h2 className="text-xl font-bold tracking-tight mb-1" style={{ color: 'var(--theme-text)' }}>
            Settings
          </h2>
          <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
            Manage your preferences
          </p>
        </div>
        
        <nav className="flex-1 px-4 pb-6">
          <div className="space-y-1">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 group"
                style={{ 
                  backgroundColor: activeSection === section.id 
                    ? 'color-mix(in srgb, var(--theme-primary) 12%, transparent)' 
                    : 'transparent'
                }}
              >
                <div 
                  className="p-2 rounded-xl transition-all"
                  style={{ 
                    backgroundColor: activeSection === section.id 
                      ? 'var(--theme-primary)' 
                      : 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
                  }}
                >
                  <section.icon 
                    className="h-4 w-4 transition-colors"
                    style={{ 
                      color: activeSection === section.id 
                        ? (currentTheme?.mode === 'dark' ? 'var(--theme-background)' : '#FFFFFF')
                        : 'var(--theme-text-secondary)'
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p 
                    className="text-sm font-semibold transition-colors"
                    style={{ 
                      color: activeSection === section.id ? 'var(--theme-text)' : 'var(--theme-text-secondary)'
                    }}
                  >
                    {section.label}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}>
                    {section.description}
                  </p>
                </div>
                <ChevronRight 
                  className="h-4 w-4 transition-all"
                  style={{ 
                    color: activeSection === section.id ? 'var(--theme-primary)' : 'transparent',
                    transform: activeSection === section.id ? 'translateX(0)' : 'translateX(-4px)'
                  }}
                />
              </button>
            ))}
          </div>
        </nav>
      </aside>

      {/* Settings Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 md:p-8 max-w-3xl">
          {activeSection === 'profile' && (
            <div className="fade-in-up">
              <div className="mb-6">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--theme-text)' }}>
                  Profile
                </h1>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  View your account information
                </p>
              </div>
              
              <div className="apple-card !p-0 overflow-hidden">
                {[
                  { label: 'Email', value: user?.email || 'N/A' },
                  { label: 'First Name', value: profile?.first_name || 'N/A' },
                  { label: 'Last Name', value: profile?.last_name || 'N/A' },
                  { 
                    label: 'Account Created', 
                    value: profile?.created_at
                      ? new Date(profile.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'
                  },
                  ...(profile?.last_login_at ? [{
                    label: 'Last Login',
                    value: new Date(profile.last_login_at).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }] : [])
                ].map((item, index, arr) => (
                  <div 
                    key={item.label}
                    className="flex items-center justify-between px-5 py-4"
                    style={{ 
                      borderBottom: index < arr.length - 1 
                        ? '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' 
                        : 'none'
                    }}
                  >
                    <Label className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                      {item.label}
                    </Label>
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'integrations' && (
            <div className="fade-in-up">
              <div className="mb-6">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--theme-text)' }}>
                  Integrations
                </h1>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  Connect and manage your bank accounts
                </p>
              </div>
              <BankConnectionManager />
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="fade-in-up">
              <div className="mb-6">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--theme-text)' }}>
                  Email roundups
                </h1>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  Stay in the loop with gentle, high-signal check-ins.
                </p>
              </div>

              <div className="apple-card p-5 space-y-6">
                {weeklyError && (
                  <div className="text-sm text-red-500">{weeklyError}</div>
                )}

                {loadingWeeklySettings && (
                  <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Loading weekly notification settings...</p>
                )}

                {weeklySettings && (
                  <>
                    {/* Weekly */}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
                          Weekly insights notification
                        </p>
                        <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                          Turn on to get an emailed summary of spend by category and AI insights.
                        </p>
                      </div>
                      <button
                        onClick={handleToggleWeeklyEnabled}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          weeklySettings.enabled ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        style={{ opacity: savingWeeklySettings ? 0.7 : 1 }}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                            weeklySettings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <Label className="text-xs uppercase font-semibold tracking-wide" style={{ color: 'var(--theme-text-secondary)' }}>
                          Days of week
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {WEEKDAY_OPTIONS.map(day => {
                            const isSelected = weeklySettings.days.includes(day.id);
                            return (
                              <button
                                key={day.id}
                                onClick={() => toggleDay(day.id)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                  isSelected ? 'shadow-sm' : ''
                                }`}
                                style={{
                                  backgroundColor: isSelected ? 'var(--theme-primary)' : 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                                  color: isSelected
                                    ? currentTheme?.mode === 'dark'
                                      ? 'var(--theme-background)'
                                      : '#FFFFFF'
                                    : 'var(--theme-text)',
                                  border: isSelected ? '1px solid var(--theme-primary)' : '1px solid transparent'
                                }}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                          <button
                            onClick={selectAllDays}
                            className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                            style={{
                              backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                              color: 'var(--theme-text)'
                            }}
                          >
                            Every day
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-xs uppercase font-semibold tracking-wide" style={{ color: 'var(--theme-text-secondary)' }}>
                          Hour to send
                        </Label>
                        <div className="flex items-center gap-2">
                          <select
                            value={weeklySettings.send_hour}
                            onChange={(e) => handleHourChange(Number(e.target.value))}
                            className="flex-1 rounded-lg border px-3 py-2 text-sm"
                            style={{
                              borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                              backgroundColor: 'var(--theme-background)',
                              color: 'var(--theme-text)'
                            }}
                          >
                            {hourOptions.map(hour => (
                              <option key={hour} value={hour}>{formatHour(hour)}</option>
                            ))}
                          </select>
                          <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)', color: 'var(--theme-text-secondary)' }}>
                            {weeklySettings.timezone}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                      <span>
                        {weeklySettings.last_sent_at
                          ? `Last sent ${new Date(weeklySettings.last_sent_at).toLocaleString()}`
                          : 'No weekly summary sent yet.'}
                      </span>
                    </div>
                  </>
                )}

                <div
                  className="pt-4"
                  style={{ borderTop: '1px solid color-mix(in srgb, var(--theme-text-secondary) 12%, transparent)' }}
                >
                  <div className="mb-3">
                    <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                      Monthly roundup
                    </p>
                    <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                      A calm monthly check‑in (sent on the 2nd in your timezone).
                    </p>
                  </div>

                  {monthlyError && (
                    <div className="text-sm text-red-500 mb-2">{monthlyError}</div>
                  )}

                  {loadingMonthlySettings && (
                    <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Loading monthly settings...</p>
                  )}

                  {monthlySettings && (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
                            Monthly roundup email
                          </p>
                          <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                            Turn on to get a monthly summary with a single next step.
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            if (!monthlySettings || savingMonthlySettings) return;
                            const next = { ...monthlySettings, enabled: !monthlySettings.enabled };
                            // Optimistic UI
                            setMonthlySettings(next);
                            setSavingMonthlySettings(true);
                            setMonthlyError(null);
                            const payload: UpdateMonthlyRoundupSettingsRequest = {
                              enabled: next.enabled,
                              send_hour: next.send_hour,
                              timezone: next.timezone,
                            };
                            try {
                              const response = await apiService.updateMonthlyRoundupSettings(payload);
                              setMonthlySettings(response.settings);
                            } catch (error) {
                              console.error('Failed to save monthly settings', error);
                              // Revert UI on failure
                              setMonthlySettings(monthlySettings);
                              setMonthlyError('Failed to save monthly roundup settings.');
                            } finally {
                              setSavingMonthlySettings(false);
                            }
                          }}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            monthlySettings.enabled ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                          style={{ opacity: savingMonthlySettings ? 0.7 : 1 }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              monthlySettings.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between text-xs mt-3" style={{ color: 'var(--theme-text-secondary)' }}>
                        <span>
                          {monthlySettings.last_sent_at
                            ? `Last sent ${new Date(monthlySettings.last_sent_at).toLocaleString()}`
                            : 'No monthly roundup sent yet.'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'themes' && (
            <ThemesSection
              currentTheme={currentTheme}
              availableThemes={availableThemes}
              updatingTheme={updatingTheme}
              themeModePreference={profile?.theme_mode ?? 'system'}
              onThemeClick={handleThemeClick}
            />
          )}
        </div>
      </main>
    </div>
  );
};
